/**
 * Area `survey-questionnaire` of the console gateway — over `apps/api/src/routes/survey-questionnaire/` (plan-030 unit
 * **WAPP-Q**; design §41.5, §41.10; glossary §6.45; ADR 0084, proposed). The survey question editor's four operations:
 *
 * ```
 *   readQuestionnaireDocument     `getVoiceAgentQuestionnaireDocument`    GET  /v1/agents/{id}/questionnaire-document
 *                                                                          ETag = the shown agent_version_id
 *   checkQuestionnaireDocument    `checkVoiceAgentQuestionnaireDocument`  POST …/questionnaire-document/check  If-Match
 *   saveQuestionnaireDocument     `setVoiceAgentQuestionnaireDocument`    PUT  …/questionnaire-document        If-Match
 *   downloadQuestionnaireFile     `getVoiceAgentQuestionnaireFile`        GET  …/questionnaire-document/file   audited
 * ```
 *
 * THE ETAG IS THE SHOWN VERSION'S ID, AND IT IS IN THE BODY. The generated client's `ApiResult` carries no response
 * header, so a check and a save name `agent_version_id` from the read's body — the same value the `ETag` quotes — and a
 * save's answer names the draft (`draft_agent_version_id`) a further save continues.
 *
 * THE QUESTIONNAIRE FILES ARE CARRIED AS JSON. `survey` is one questionnaire file per language exactly as stored (a
 * first-format file arrives already moved to the current one, with `format_converted` and `wording_added` in
 * `warning`); the editor (`../../routes/agents/[id]/questions/questionnaire-model.ts`) edits it as JSON so every key it
 * does not draw — keywords, notes, recognition terms, count words — is sent back untouched.
 *
 * A REFUSAL KEEPS ITS DETAIL. `questionnaire_invalid` carries every problem in `error.detail.problem` (and the warnings),
 * `questionnaire_base_stale` the version the editor edits now in `error.detail.editable`; the generated `ApiError` drops
 * `detail`, so this slice raises {@link QuestionnaireRefusedError} (an `ApiError`, the same code, status and field) with
 * them read, so the Questions tab can put each problem on its card and field.
 *
 * The register is EMPTY: every operation is in the assembled spec. The sandbox half is `../sandbox/survey-questionnaire.ts`.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ApiError, assertOk, type WeTalkApiClient } from "@wetalk/api-client";

import type { ConsoleGateway } from "../gateway.js";
import { MalformedResponseError, readArray, readBoolean, readRecord, readText } from "../decode.js";
import { useGateway } from "../provider.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

export const QUESTIONNAIRE_NOT_EDITABLE_REASON = [
  "editor_unavailable",
  "not_the_editable_version",
  "building",
  "not_a_questionnaire",
  "voice_agent_archived",
] as const;
export type QuestionnaireNotEditableReason = (typeof QUESTIONNAIRE_NOT_EDITABLE_REASON)[number];

export const QUESTIONNAIRE_FORMAT = ["surveybot-survey-json-v1", "wetalk-survey-json-v2", "wetalk-survey-json-v3"] as const;
export type QuestionnaireFormat = (typeof QUESTIONNAIRE_FORMAT)[number];

export const QUESTIONNAIRE_QUESTION_TYPE = ["scale", "choice", "open", "number"] as const;
export type QuestionnaireQuestionType = (typeof QUESTIONNAIRE_QUESTION_TYPE)[number];

export const QUESTIONNAIRE_FILE_FORMAT = ["json", "md"] as const;
export type QuestionnaireFileFormatView = (typeof QUESTIONNAIRE_FILE_FORMAT)[number];

/** One language's questionnaire file, as JSON (`QuestionnaireFile`). */
export type QuestionnaireFileJson = Readonly<Record<string, unknown>>;

/** language code → its questionnaire file. */
export type QuestionnaireSurveyJson = Readonly<Record<string, QuestionnaireFileJson>>;

/** One problem or warning (`QuestionnaireProblem` / `QuestionnaireWarning`): a stable code and the path it is at. */
export interface QuestionnaireNoteView {
  readonly code: string;
  /** `survey.el.variants.A.questions.3.options`. */
  readonly path: string;
  /** English, for API clients. The portal words each code itself. */
  readonly message: string;
  readonly detail: Readonly<Record<string, unknown>>;
}

/** The six words wetalk adds for a new question in one language (`pack_phrase`). */
export interface QuestionnairePackPhraseView {
  readonly questionOrdinal: string;
  readonly lastQuestionOrdinal: string;
  readonly clarifyChoice: string;
  readonly clarifyOpen: string;
  readonly clarifyNumber: string;
  readonly choiceMore: string;
}

/** The lines wetalk says itself in one language (`platform_line`), shown read-only. */
export interface QuestionnairePlatformLineView {
  readonly aiIdentity: string | null;
  readonly unknownAnswer: string | null;
  readonly recordingNotice: string | null;
}

export interface QuestionCodeHistoryView {
  readonly questionCode: string;
  readonly questionType: QuestionnaireQuestionType;
  readonly firstAgentVersionLabel: string | null;
}

export interface CampaignDiallingView {
  readonly campaignId: string;
  readonly name: string;
  readonly state: "scheduled" | "running";
}

/** `QuestionnaireDocument`: one version's questions as the editor shows them. */
export interface QuestionnaireDocumentView {
  readonly agentVersionId: string | null;
  readonly agentVersionLabel: string | null;
  readonly isDraft: boolean;
  readonly baseAgentVersionId: string | null;
  readonly editable: boolean;
  readonly notEditableReason: QuestionnaireNotEditableReason | null;
  readonly ruleAvailable: boolean;
  readonly instructionReadingAvailable: boolean;
  readonly format: QuestionnaireFormat | null;
  readonly survey: QuestionnaireSurveyJson | null;
  readonly warning: readonly QuestionnaireNoteView[];
  readonly questionCodeHistory: readonly QuestionCodeHistoryView[];
  readonly platformLine: Readonly<Record<string, QuestionnairePlatformLineView | null>>;
  readonly packPhrase: Readonly<Record<string, QuestionnairePackPhraseView | null>>;
  readonly campaignDialling: readonly CampaignDiallingView[];
}

/** `QuestionnaireDocumentCheck`: what a save would answer, without the write. */
export interface QuestionnaireCheckView {
  readonly problem: readonly QuestionnaireNoteView[];
  readonly warning: readonly QuestionnaireNoteView[];
  readonly changed: boolean;
  readonly freeCode: string;
  /** The files with the words wetalk added, as far as they could be read. */
  readonly survey: QuestionnaireSurveyJson | null;
}

/** `QuestionnaireDocumentSaved`. */
export interface QuestionnaireSavedView {
  readonly changed: boolean;
  readonly draftAgentVersionId: string | null;
  readonly draftLabel: string | null;
  readonly baseAgentVersionId: string | null;
  readonly warning: readonly QuestionnaireNoteView[];
}

/** A save: every language's whole file, and the draft's note on Versions. */
export interface QuestionnaireChange {
  readonly survey: QuestionnaireSurveyJson;
  readonly changeNote: string | null;
}

/** A downloaded file: its text and the media type to save it as. */
export interface QuestionnaireFileView {
  readonly text: string;
  readonly contentType: string;
}

/**
 * A questionnaire refusal with its detail: `problem` and `warning` for `questionnaire_invalid`, `editable` (the version
 * the editor edits now) for `questionnaire_base_stale`.
 */
export class QuestionnaireRefusedError extends ApiError {
  readonly problem: readonly QuestionnaireNoteView[];
  readonly warning: readonly QuestionnaireNoteView[];
  readonly editable: { readonly agentVersionId: string; readonly agentVersionLabel: string } | null;
  constructor(status: number, body: unknown, requestId: string | null) {
    super(status, body, requestId);
    const detail = (body as { error?: { detail?: unknown } } | null)?.error?.detail;
    const record = typeof detail === "object" && detail !== null ? (detail as Readonly<Record<string, unknown>>) : {};
    this.problem = Array.isArray(record["problem"]) ? notesOf(record["problem"], "error.detail.problem") : [];
    this.warning = Array.isArray(record["warning"]) ? notesOf(record["warning"], "error.detail.warning") : [];
    const editable = record["editable"];
    if (typeof editable === "object" && editable !== null) {
      const row = editable as Readonly<Record<string, unknown>>;
      this.editable = {
        agentVersionId: readText(row["agent_version_id"], "error.detail.editable.agent_version_id"),
        agentVersionLabel: readText(row["agent_version_label"], "error.detail.editable.agent_version_label"),
      };
    } else {
      this.editable = null;
    }
  }
}

/** The refusal of `raised` when it is a questionnaire refusal with a detail, else `null`. */
export function questionnaireRefusalOf(raised: unknown): QuestionnaireRefusedError | null {
  return raised instanceof QuestionnaireRefusedError ? raised : null;
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /**
     * plan-030 WAPP-Q: a survey VoiceAgent's questions — the version the editor edits, or (`agentVersionId`) another
     * version, read-only.
     */
    readQuestionnaireDocument(voiceAgentId: string, agentVersionId: string | null): Promise<QuestionnaireDocumentView>;
    /** plan-030 WAPP-Q: the save's checks without a write, naming the version edited (`If-Match`). */
    checkQuestionnaireDocument(voiceAgentId: string, agentVersionId: string, change: QuestionnaireChange): Promise<QuestionnaireCheckView>;
    /**
     * plan-030 WAPP-Q: save edited questions as a draft. Raises {@link QuestionnaireRefusedError} for
     * `questionnaire_invalid` (every problem) and `questionnaire_base_stale` (the version edited now).
     */
    saveQuestionnaireDocument(voiceAgentId: string, agentVersionId: string, change: QuestionnaireChange): Promise<QuestionnaireSavedView>;
    /** plan-030 WAPP-Q: one version's questions as a file (audited by the api). */
    downloadQuestionnaireFile(
      voiceAgentId: string,
      agentVersionId: string,
      fileFormat: QuestionnaireFileFormatView,
      languageCode: string | null,
    ): Promise<QuestionnaireFileView>;
  }
}

/** The methods this slice puts on `ConsoleGateway`. */
export type SurveyQuestionnaireGatewaySlice = Pick<
  ConsoleGateway,
  "readQuestionnaireDocument" | "checkQuestionnaireDocument" | "saveQuestionnaireDocument" | "downloadQuestionnaireFile"
>;

/** This area's rows of `GATEWAY_GAP`: none — every operation is served. */
export const surveyQuestionnaireGap: readonly GatewayGap[] = [];

// ── the wire ─────────────────────────────────────────────────────────────────────────────────────────────

function oneOf<T extends string>(allowed: readonly T[], value: unknown, path: string): T {
  const raw = readText(value, path);
  const found = allowed.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, allowed.join(" | "), raw);
  return found;
}

function nullableText(value: unknown, path: string): string | null {
  return value === null || value === undefined ? null : readText(value, path);
}

function notesOf(value: unknown, path: string): readonly QuestionnaireNoteView[] {
  return readArray(value, path).map((one, index) => {
    const at = `${path}[${String(index)}]`;
    const row = readRecord(one, at);
    const detail = row["detail"];
    return {
      code: readText(row["code"], `${at}.code`),
      path: readText(row["path"], `${at}.path`),
      message: readText(row["message"], `${at}.message`),
      detail: typeof detail === "object" && detail !== null && !Array.isArray(detail) ? (detail as Readonly<Record<string, unknown>>) : {},
    };
  });
}

function surveyOf(value: unknown, path: string): QuestionnaireSurveyJson | null {
  if (value === null || value === undefined) return null;
  const record = readRecord(value, path);
  return Object.fromEntries(Object.entries(record).map(([language, file]) => [language, readRecord(file, `${path}.${language}`)]));
}

function byLanguageOf<T>(value: unknown, path: string, read: (row: Readonly<Record<string, unknown>>, at: string) => T): Readonly<Record<string, T | null>> {
  const record = readRecord(value, path);
  return Object.fromEntries(
    Object.entries(record).map(([language, row]) => [language, row === null ? null : read(readRecord(row, `${path}.${language}`), `${path}.${language}`)]),
  );
}

/** One `QuestionnaireDocument`. */
export function questionnaireDocumentOf(wire: unknown, path: string): QuestionnaireDocumentView {
  const data = readRecord(wire, path);
  return {
    agentVersionId: nullableText(data["agent_version_id"], `${path}.agent_version_id`),
    agentVersionLabel: nullableText(data["agent_version_label"], `${path}.agent_version_label`),
    isDraft: readBoolean(data["is_draft"], `${path}.is_draft`),
    baseAgentVersionId: nullableText(data["base_agent_version_id"], `${path}.base_agent_version_id`),
    editable: readBoolean(data["editable"], `${path}.editable`),
    notEditableReason:
      data["not_editable_reason"] === null ? null : oneOf(QUESTIONNAIRE_NOT_EDITABLE_REASON, data["not_editable_reason"], `${path}.not_editable_reason`),
    ruleAvailable: readBoolean(data["rule_available"], `${path}.rule_available`),
    instructionReadingAvailable: readBoolean(data["instruction_reading_available"], `${path}.instruction_reading_available`),
    format: data["format"] === null ? null : oneOf(QUESTIONNAIRE_FORMAT, data["format"], `${path}.format`),
    survey: surveyOf(data["survey"], `${path}.survey`),
    warning: notesOf(data["warning"], `${path}.warning`),
    questionCodeHistory: readArray(data["question_code_history"], `${path}.question_code_history`).map((one, index) => {
      const at = `${path}.question_code_history[${String(index)}]`;
      const row = readRecord(one, at);
      return {
        questionCode: readText(row["question_code"], `${at}.question_code`),
        questionType: oneOf(QUESTIONNAIRE_QUESTION_TYPE, row["question_type"], `${at}.question_type`),
        firstAgentVersionLabel: nullableText(row["first_agent_version_label"], `${at}.first_agent_version_label`),
      };
    }),
    platformLine: byLanguageOf(data["platform_line"], `${path}.platform_line`, (row, at) => ({
      aiIdentity: nullableText(row["ai_identity"], `${at}.ai_identity`),
      unknownAnswer: nullableText(row["unknown_answer"], `${at}.unknown_answer`),
      recordingNotice: nullableText(row["recording_notice"], `${at}.recording_notice`),
    })),
    packPhrase: byLanguageOf(data["pack_phrase"], `${path}.pack_phrase`, (row, at) => ({
      questionOrdinal: readText(row["question_ordinal"], `${at}.question_ordinal`),
      lastQuestionOrdinal: readText(row["last_question_ordinal"], `${at}.last_question_ordinal`),
      clarifyChoice: readText(row["clarify_choice"], `${at}.clarify_choice`),
      clarifyOpen: readText(row["clarify_open"], `${at}.clarify_open`),
      clarifyNumber: readText(row["clarify_number"], `${at}.clarify_number`),
      choiceMore: readText(row["choice_more"], `${at}.choice_more`),
    })),
    campaignDialling: readArray(data["campaign_dialling"], `${path}.campaign_dialling`).map((one, index) => {
      const at = `${path}.campaign_dialling[${String(index)}]`;
      const row = readRecord(one, at);
      return {
        campaignId: readText(row["campaign_id"], `${at}.campaign_id`),
        name: readText(row["name"], `${at}.name`),
        state: oneOf(["scheduled", "running"] as const, row["state"], `${at}.state`),
      };
    }),
  };
}

/** One `QuestionnaireDocumentCheck`. */
export function questionnaireCheckOf(wire: unknown, path: string): QuestionnaireCheckView {
  const data = readRecord(wire, path);
  return {
    problem: notesOf(data["problem"], `${path}.problem`),
    warning: notesOf(data["warning"], `${path}.warning`),
    changed: readBoolean(data["changed"], `${path}.changed`),
    freeCode: readText(data["free_code"], `${path}.free_code`),
    survey: surveyOf(data["survey"], `${path}.survey`),
  };
}

/** One `QuestionnaireDocumentSaved`. */
export function questionnaireSavedOf(wire: unknown, path: string): QuestionnaireSavedView {
  const data = readRecord(wire, path);
  return {
    changed: readBoolean(data["changed"], `${path}.changed`),
    draftAgentVersionId: nullableText(data["draft_agent_version_id"], `${path}.draft_agent_version_id`),
    draftLabel: nullableText(data["draft_label"], `${path}.draft_label`),
    baseAgentVersionId: nullableText(data["base_agent_version_id"], `${path}.base_agent_version_id`),
    warning: notesOf(data["warning"], `${path}.warning`),
  };
}

function dataOf(body: unknown, operationId: string): unknown {
  return readRecord(body, operationId)["data"];
}

function bodyOf(change: QuestionnaireChange): Readonly<Record<string, unknown>> {
  return change.changeNote === null ? { survey: change.survey } : { survey: change.survey, change_note: change.changeNote };
}

/** Raised as {@link QuestionnaireRefusedError} so the detail travels; anything else as the generated `ApiError`. */
const REFUSAL_WITH_DETAIL: readonly string[] = ["questionnaire_invalid", "questionnaire_base_stale"];

const FILE_CONTENT_TYPE: Readonly<Record<QuestionnaireFileFormatView, string>> = {
  json: "application/json",
  md: "text/markdown",
};

export function surveyQuestionnaireApiSlice(client: WeTalkApiClient, _context: SliceContext): SurveyQuestionnaireGatewaySlice {
  const refuseWithDetail = (answer: { readonly status: number; readonly body: unknown; readonly requestId: string | null }): void => {
    if (answer.status < 400) return;
    const refused = new QuestionnaireRefusedError(answer.status, answer.body, answer.requestId);
    if (REFUSAL_WITH_DETAIL.includes(refused.code)) throw refused;
  };
  return {
    async readQuestionnaireDocument(voiceAgentId, agentVersionId) {
      const answer = await client.getVoiceAgentQuestionnaireDocument({
        path: { voice_agent_id: voiceAgentId },
        ...(agentVersionId === null ? {} : { query: { agent_version_id: agentVersionId } }),
      });
      return questionnaireDocumentOf(dataOf(assertOk(answer), "getVoiceAgentQuestionnaireDocument"), "getVoiceAgentQuestionnaireDocument.data");
    },
    async checkQuestionnaireDocument(voiceAgentId, agentVersionId, change) {
      const answer = await client.checkVoiceAgentQuestionnaireDocument({
        path: { voice_agent_id: voiceAgentId },
        body: bodyOf(change),
        header: { "if-match": `"${agentVersionId}"` },
      });
      refuseWithDetail(answer);
      return questionnaireCheckOf(dataOf(assertOk(answer), "checkVoiceAgentQuestionnaireDocument"), "checkVoiceAgentQuestionnaireDocument.data");
    },
    async saveQuestionnaireDocument(voiceAgentId, agentVersionId, change) {
      const answer = await client.setVoiceAgentQuestionnaireDocument({
        path: { voice_agent_id: voiceAgentId },
        body: bodyOf(change),
        header: { "if-match": `"${agentVersionId}"` },
      });
      refuseWithDetail(answer);
      return questionnaireSavedOf(dataOf(assertOk(answer), "setVoiceAgentQuestionnaireDocument"), "setVoiceAgentQuestionnaireDocument.data");
    },
    async downloadQuestionnaireFile(voiceAgentId, agentVersionId, fileFormat, languageCode) {
      const answer = await client.getVoiceAgentQuestionnaireFile({
        path: { voice_agent_id: voiceAgentId },
        query: { agent_version_id: agentVersionId, file_format: fileFormat, ...(languageCode === null ? {} : { language_code: languageCode }) },
      });
      const body = assertOk(answer);
      // The client parses a JSON body; the stored file is re-serialised as the export writes it (two-space indent).
      const text = typeof body === "string" ? body : `${JSON.stringify(body, null, 2)}\n`;
      return { text, contentType: FILE_CONTENT_TYPE[fileFormat] };
    },
  };
}

// ── the read the tab uses ────────────────────────────────────────────────────────────────────────────────

/** Query keys of this area, per VoiceAgent and shown version. */
export const SURVEY_QUESTIONNAIRE_QUERY_KEY = {
  all: ["survey-questionnaire"] as const,
  document: (voiceAgentId: string, agentVersionId: string | null) => ["survey-questionnaire", voiceAgentId, agentVersionId ?? "editable"] as const,
};

/** The questions of the editable version (or of `agentVersionId`). A failed read leaves `data` undefined. */
export function useQuestionnaireDocument(voiceAgentId: string, agentVersionId: string | null): UseQueryResult<QuestionnaireDocumentView> {
  const gateway = useGateway();
  return useQuery({
    queryKey: SURVEY_QUESTIONNAIRE_QUERY_KEY.document(voiceAgentId, agentVersionId),
    queryFn: () => gateway.readQuestionnaireDocument(voiceAgentId, agentVersionId),
    retry: false,
  });
}

/**
 * Saves a downloaded file in the browser. The name is composed here because the client does not carry the answer's
 * `Content-Disposition`: `<name>-v<n>-questions.<json|md>`, the shape the api names it by.
 */
export function saveQuestionnaireFile(file: QuestionnaireFileView, fileName: string): void {
  const url = URL.createObjectURL(new Blob([file.text], { type: `${file.contentType};charset=utf-8` }));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}
